import { FileActions } from 'components/newListeningExperience/topnav/FileAction';
import { AskAIUserMessageOptions } from 'modules/ask-ai/types';
import type { BooleanListeningSettingKey, SkipContentAnalyticsTypes } from 'modules/listening/features/settings/settings';
import { ListeningScreenTabs } from 'modules/listening/navStore';
import { ContentMetaType, ContentMetaTypeLowercase } from 'modules/sdk/lib/facade/listenableContent/content.types';

import { AudioDownloadAnalyticsEventProperties } from './event-types/audioDownload';
import { NoteHighlightAnalyticsEventProperties } from './event-types/notesAndHighlights';
import { TableOfContentsAnalyticsEventProperties } from './event-types/tableOfContents';
import { VoicesLibraryAnalyticsEventProperties } from './event-types/voicesLibrary';

export enum AnalyticsEventKey {
  aiShortcutChatButtonClicked = 'web_app_ai_shortcut_chat_button_clicked',
  aiShortcutSummaryButtonClicked = 'web_app_ai_shortcut_summary_button_clicked',
  aiShortcutQuizButtonClicked = 'web_app_ai_shortcut_quiz_button_clicked',
  aiShortcutPodcastButtonClicked = 'web_app_ai_shortcut_podcast_button_clicked',
  aiShortcutRecapButtonClicked = 'web_app_ai_shortcut_recap_button_clicked',
  askAISummaryFeedbackSubmitted = 'web_app_document_summary_feedback_submitted',
  askAISummaryRatingSubmitted = 'web_app_document_summary_rating_submitted',
  changedVoiceTab = 'web_app_changed_voice_tab',
  documentAskAi = 'web_app_document_ask_ai',
  documentAskAiAutoplayResponses = 'web_app_document_ask_ai_autoplay_responses',
  documentAskAiSuggestionsToggled = 'web_app_document_ask_ai_suggestions_toggled',
  documentAskAiChatCleared = 'web_app_document_ask_ai_chat_cleared',
  documentAskAiClearChatModalOpened = 'web_app_document_ask_ai_clear_chat_modal_opened',
  documentAskAiCloseChat = 'web_app_document_ask_ai_close_chat',
  documentAskAiListenedToAnswer = 'web_app_document_ask_ai_listened_to_answer',
  documentAskAiListenedToQuestion = 'web_app_document_ask_ai_listened_to_quiz_question',
  documentAskAiCopyAnswer = 'web_app_document_ask_ai_copy_answer',
  documentCompleted = 'web_app_document_completed',
  documentDeleted = 'web_app_document_deleted',
  documentOpened = 'web_app_document_opened',
  documentOpenedDefault = 'web_app_default_document_opened',
  documentOpenedRelisten = 'web_app_document_opened_relisten',
  documentSummaryClicked = 'web_app_document_summary_clicked',
  documentActionsMenuClicked = 'web_app_document_actions_menu_clicked',
  epubUpsellAddButtonClicked = 'web_epub_upsell_modal_doc_add_btn_clicked',
  documentActionsMenuItemClicked = 'web_app_document_actions_menu_item_clicked',
  epubUpsellModalDismissed = 'web_epub_upsell_modal_dismissed',
  helpCenterFeatureRequest = 'web_help_center_feature_request',
  helpCenterMessageUpgrade = 'web_help_center_message_upgrade',
  helpCenterReportBrokenExperience = 'web_help_center_report_broken_experience',
  helpUsImprove = 'web_help_us_improve',
  homeButtonPressed = 'web_app_home_button_pressed',
  instantListeningUploadWarningModalConfirmButtonClicked = 'web_app_instant_listening_upload_warning_modal_confirm_button_clicked',
  instantListeningUploadWarningModalDismissButtonClicked = 'web_app_instant_listening_upload_warning_modal_dismiss_button_clicked',
  instantListeningUploadWarningModalShown = 'web_app_instant_listening_upload_warning_modal_shown',
  listeningFeedbackContent = 'listening_feedback_content',
  listeningFeedbackRating = 'listening_feedback_rating',

  listeningSettingsClicked = 'web_app_listening_settings_clicked',
  listeningSettingsFontFamilyChanged = 'web_app_listening_settings_font_family_changed',
  listeningSettingsSkipContentClicked = 'clicked_skip_content',
  listeningSettingsZoomChanged = 'web_app_listening_settings_zoom_changed',
  listeningSettingsThemeChanged = 'web_app_listening_settings_theme_changed',
  listeningSettingsCursorColorChanged = 'web_app_listening_settings_cursor_color_changed',
  listeningSettingsHighlightSentenceChanged = 'web_app_listening_settings_highlight_sentence_changed',
  listeningSettingsAutoSkipModeChanged = 'web_app_listening_settings_auto_skip_mode_changed',

  // MP3 Download
  mp3DownloadModalOpened = 'mp3_download_modal_opened',
  mp3DownloadVoiceSelected = 'mp3_download_voice_selected',
  mp3DownloadScopeSelected = 'mp3_download_scope_selected',
  mp3DownloadStarted = 'mp3_download_started',
  mp3DownloadUpgradeTrial = 'mp3_download_upgrade_trial',
  mp3DownloadUpgradeTrialFailed = 'mp3_download_upgrade_trial_failed',
  mp3DownloadUpgradeTrialSucceed = 'mp3_download_upgrade_trial_succeed',
  mp3DownloadLimitReached = 'mp3_download_limit_reached',
  mp3DownloadCompleted = 'mp3_download_completed',
  mp3DownloadModalClosed = 'mp3_download_modal_closed',
  mp3DownloadError = 'mp3_download_error',
  mp3DownloadRateLimited = 'web_app_mp3_download_rate_limited',

  accountPage = 'web_app_account_page_upgrade',
  askAI = 'web_app_ask_ai_upgrade',
  checkoutModalOpened = 'checkout_modal_opened',
  downloadMp3v2 = 'web_app_download_mp3_v2_upgrade',
  openedDocumentSettings = 'opened_document_settings',
  pdfLimitPopup = 'web_app_pdf_limit_popup',
  playerJumpBySentence = 'web_app_jump_by_sentence',
  playerSettings = 'web_app_player_settings',
  playerSpeedChanged = 'web_app_speed_changed',
  playerSpeedRampChanged = 'web_app_speed_ramp_changed',
  playerVoiceChanged = 'web_app_voice_changed',
  premiumVoices = 'web_app_premium_voices_upgrade',
  profile = 'web_app_profile_upgrade',
  sidebar = 'web_app_sidebar_upgrade',
  skipHeaderFooterUpsell = 'web_app_skip_header_footer_upsell',
  speedControls = 'web_app_speed_controls_upgrade',
  speedPickerUpsell = 'web_app_speed_picker_upsell',
  subscriptionSettings = 'web_app_subscription_settings_upgrade',
  switchedToFree = 'web_app_switched_to_free',
  topNavItemClicked = 'web_app_top_nav_item_clicked',
  unlimitedFiles = 'web_app_unlimited_files_upgrade',
  unlimitedSpeedPopup = 'web_app_unlimited_speed_popup',
  upgradePressedAudioVisualCta = 'web_app_audio_visual_cta_upgrade_pressed',
  upgradePressedContextual = 'web_app_contextual_upsell_upgrade_clicked',
  upgradePressedContextualControl = 'web_app_contextual_upsell_control_upgrade_clicked',
  upgradePressedDashboard = 'web_app_dashboard_upgrade_clicked',
  upgradePressedOcrUpsell = 'web_app_ocr_upsell_upgrade_clicked',
  upsellModalsAudioVisualCtaClosed = 'web_app_audio_visual_cta_closed',
  upsellModalsContextual = 'web_app_contextual_upsell_shown',
  upsellModalsOcrUpsellShown = 'web_app_ocr_upsell_shown',
  userProfileTabChanged = 'user_profile_tab_changed',

  // User Settings Page
  gamificationSetDailyGoal = 'gamification_set_daily_goal',
  gamificationDailyGoalPopupOpened = 'gamification_set_daily_goal_popup_opened',
  discoverAppsOpened = 'web_app_settings_discover_speechify_apps_opened',
  settingsShareIdeaPressed = 'web_app_settings_share_an_idea_pressed',
  settingsGetHelpPressed = 'web_app_settings_get_help_pressed',
  deletedFilesOpened = 'web_app_deleted_files_opened',
  statsOpened = 'web_app_stats_page_opened',
  statsTimePeriodChanged = 'web_app_stats_page_time_period_changed',
  settingsFileSuggestionChanged = 'web_app_settings_file_suggestion_changed',
  settingsInstantListeningChanged = 'web_app_settings_instant_listening_changed',
  settingsLanguageChanged = 'web_app_settings_language_changed',
  cancellationFeedback = 'web_app_cancellation_feedback',
  cancellationBegan = 'web_app_began_cancellation',
  cancellationError = 'web_app_cancellation_error',
  cancellationReason = 'web_app_cancellation_reason',
  cancellationDiscountSelection = 'web_app_cancellation_discount_selection',
  resumePremiumPressed = 'web_app_subscription_resume_pressed',
  resumePremiumPressedError = 'web_app_resume_pressed_error',
  openBillingDashboard = 'web_app_open_billing_dashboard',

  // User Authentication
  userAuthenticationLegacy = 'web_app_user_authentication',
  voiceoverLinkClicked = 'voiceover_link_clicked',
  signOutPressed = 'web_app_sign_out_pressed',
  settingsPressed = 'web_app_settings_opened',
  privacyPressed = 'web_app_privacy_opened',
  routeVisited = 'web_app_route_visited',
  termsPressed = 'web_app_terms_opened',
  userAuthenticationFailed = 'User Authentication Failed',
  userSignedIn = 'User Signed In',
  userSignedOut = 'User Signed Out',
  userDeletedAccount = 'user_deleted_account',
  voiceControls = 'web_app_voice_controls_upgrade',
  voiceSelector = 'web_app_voice_selector_upgrade',

  // Library
  libraryFileUploadStarted = 'web_app_library_file_upload_started',
  libraryFileUploadFailed = 'web_app_library_file_upload_failed',
  libraryFileReuploadStarted = 'web_app_library_file_reupload_started',
  libraryFileUploadCancelled = 'web_app_library_file_upload_cancelled',
  libraryImportErrorFileDeleted = 'web_app_library_import_error_file_deleted',
  libraryImportDialogSourceOpened = 'web_app_library_import_dialog_source_opened',
  libraryIntegrationFileImportStarted = 'web_app_library_integration_file_import_started',
  libraryIntegrationFileImportFailed = 'web_app_library_integration_file_import_failed',
  librarySuggestionImportClicked = 'web_app_library_suggestion_import_clicked',
  libraryImportSuggestionDismissed = 'web_app_library_import_suggestion_dismissed',
  libraryItemsMoved = 'web_app_library_items_moved',
  libraryItemsRemoved = 'web_app_library_items_removed',
  librarySortingChanged = 'web_app_library_sorting_changed',
  libraryFilterChanged = 'web_app_library_filter_changed',
  libraryViewModeChanged = 'web_app_library_view_mode_changed',
  libraryShowFolderTypeChanged = 'web_app_library_show_folder_type_changed',
  libraryThemeToggled = 'web_app_library_theme_toggled',
  libraryBreadcrumbsNavigated = 'web_app_library_breadcrumbs_navigated',
  libraryUpgradeClicked = 'web_app_library_upgrade_clicked',
  libraryItemRenamed = 'web_app_library_item_renamed',
  libraryFileShared = 'web_app_library_file_shared',
  libraryShareDocument = 'web_app_library_share_document',
  libraryFileOriginalUrlOpened = 'web_app_library_file_original_url_opened',
  libraryFolderCreated = 'web_app_library_folder_created',
  libraryContextMenuOpened = 'web_app_library_context_menu_opened',
  libraryContextMenuClosed = 'web_app_library_context_menu_closed',
  libraryContextMenuActionClicked = 'web_app_library_context_menu_action_clicked',

  // Voiceover
  voiceoverCrossSellPressed = 'web_app_voiceover_cross_sell_pressed',

  // Voices library
  voicesLibraryVoiceChanged = 'web_app_voices_library_voice_changed',
  voicesLibraryOpened = 'web_app_voices_library_opened',
  voicesLibraryClosed = 'web_app_voices_library_closed',
  voicesLibraryCloningOpened = 'web_app_voices_library_cloning_opened',
  voicesLibraryCloningCompleted = 'web_app_voices_library_cloning_completed',
  voicesLibraryCloneRemoved = 'web_app_voices_library_clone_removed',
  voicesLibraryFavoriteAdded = 'web_app_voices_library_favorite_added',
  voicesLibraryFavoriteRemoved = 'web_app_voices_library_favorite_removed',
  voicesLibraryPaywallOpened = 'web_app_voices_library_paywall_opened',
  voicesLibrarySidebarOpened = 'web_app_voices_library_sidebar_opened',
  voicesLibraryPreviewPlayed = 'web_app_voices_library_preview_played',
  voicesLibraryFiltersChanged = 'web_app_voices_library_filters_changed',
  voicesLibrarySimilarVoiceClicked = 'web_app_voices_library_similar_voice_clicked',

  // Table of Contents
  tableOfContentsOpened = 'web_app_toc_sidebar_clicked',
  tableOfContentsItemClicked = 'web_app_toc_item_selected',

  // Checkout and Subscription
  trialStarted = 'extension_start_trial_success_test',
  purchaseCompleted = 'extension_paywall_FE_buy_now',

  noteHighlightSidebarClicked = 'web_app_note_highlight_sidebar_clicked',
  noteHighlightTabClicked = 'web_app_note_highlight_tab_clicked',
  bookmarkTabClicked = 'web_app_bookmark_tab_clicked',
  bookmarkAdded = 'web_app_bookmark_added',
  bookmarkRemoved = 'web_app_bookmark_removed',
  bookmarkClicked = 'web_app_bookmark_clicked',

  noteHighlightUpdated = 'web_app_note_highlight_updated',
  noteHighlightAdded = 'web_app_note_highlight_added',
  noteHighlightRemoved = 'web_app_note_highlight_removed',
  noteHighlightFocused = 'web_app_note_highlight_focused',
  noteHighlightTextSelected = 'web_app_note_highlight_text_selected',
  noteHighlightTextCopied = 'web_app_note_highlight_text_copied',
  noteHighlightClicked = 'web_app_note_highlight_clicked'
}

export type Properties<Context extends Record<string, unknown>> = {} & Context;

type AuthEventProperties = {
  provider: string;
  flow: string;
  source_url: string;
  app_platform: string;
  app_version: string;
};

export type AnalyticsEventProperties = {
  [AnalyticsEventKey.aiShortcutChatButtonClicked]: Properties<{}>;
  [AnalyticsEventKey.aiShortcutSummaryButtonClicked]: Properties<{}>;
  [AnalyticsEventKey.aiShortcutQuizButtonClicked]: Properties<{}>;
  [AnalyticsEventKey.aiShortcutPodcastButtonClicked]: Properties<{}>;
  [AnalyticsEventKey.documentAskAi]: Properties<
    | {
        action: 'chat';
        query: string;
        options?: AskAIUserMessageOptions;
      }
    | {
        action: 'summary_regenerate';
      }
    | {
        action: 'generate_summary';
        allPages: boolean;
        length: 'short' | 'medium' | 'long';
        mode: 'paragraph' | 'keypoints';
        pageIndexes: number[];
      }
    | {
        action: 'generate_quiz';
        allPages: boolean;
        pageIndexes: number[];
        questionCount: number;
        fileType: ContentMetaType;
      }
    | { action: 'answer_quiz'; quizId: string; questionKey: string; answerIndex: number; fileType: ContentMetaType; method: 'keyboard' | 'mouse' }
    | { action: 'retake_quiz'; quizId: string; fileType: ContentMetaType }
    | {
        action: 'complete_quiz';
        quizId: string;
        fileType: ContentMetaType;
        questionCount: number;
        correctAnswerCount: number;
        percentCorrect: number;
      }
  >;
  [AnalyticsEventKey.documentAskAiAutoplayResponses]: Properties<{
    state: 'on' | 'off';
    source: 'chat' | 'quiz';
  }>;
  [AnalyticsEventKey.documentAskAiSuggestionsToggled]: Properties<{
    state: 'on' | 'off';
  }>;
  [AnalyticsEventKey.documentAskAiListenedToAnswer]: Properties<{
    via: 'autoplay' | 'button';
  }>;
  [AnalyticsEventKey.documentAskAiListenedToQuestion]: Properties<{
    fileType: ContentMetaType;
    via: 'autoplay' | 'button';
  }>;
  [AnalyticsEventKey.listeningSettingsSkipContentClicked]: Properties<{
    type: 'all' | SkipContentAnalyticsTypes;
    value: 'on' | 'off';
  }>;
  [AnalyticsEventKey.documentDeleted]: Properties<{
    type: ContentMetaTypeLowercase;
  }>;
  [AnalyticsEventKey.documentOpened]: Properties<{
    type: ContentMetaTypeLowercase;
  }>;
  [AnalyticsEventKey.documentOpenedRelisten]: Properties<{
    type: ContentMetaTypeLowercase;
  }>;
  [AnalyticsEventKey.documentSummaryClicked]: Properties<{
    state: 'collapse' | 'expanded';
    fileType: ContentMetaType;
    fileWordCount?: number;
  }>;
  [AnalyticsEventKey.documentActionsMenuClicked]: Properties<{
    from: 'listening_screen' | 'library_screen';
  }>;
  [AnalyticsEventKey.documentActionsMenuItemClicked]: Properties<{
    from: 'listening_screen' | 'library_screen';
    action: FileActions;
    documentType: ContentMetaType;
  }>;

  [AnalyticsEventKey.helpCenterFeatureRequest]: Properties<{ featureRequest: string }>;

  [AnalyticsEventKey.helpCenterReportBrokenExperience]: Properties<{ feedback: string }>;

  [AnalyticsEventKey.homeButtonPressed]: Properties<{
    source: string;
  }>;
  [AnalyticsEventKey.listeningSettingsClicked]: Properties<{
    key: BooleanListeningSettingKey;
    value: boolean;
  }>;
  [AnalyticsEventKey.listeningSettingsZoomChanged]: Properties<{
    zoomPercentage: number;
  }>;
  [AnalyticsEventKey.listeningSettingsFontFamilyChanged]: Properties<{
    fontFamily: string;
  }>;
  [AnalyticsEventKey.listeningSettingsThemeChanged]: Properties<{
    theme: string;
  }>;
  [AnalyticsEventKey.listeningSettingsCursorColorChanged]: Properties<{
    cursorColor: string;
  }>;
  [AnalyticsEventKey.listeningSettingsHighlightSentenceChanged]: Properties<{
    highlightSentence: boolean;
  }>;
  [AnalyticsEventKey.listeningSettingsAutoSkipModeChanged]: Properties<{
    autoSkipMode: string;
  }>;
  [AnalyticsEventKey.listeningFeedbackRating]: Properties<{
    sentiment: number;
    // In old FeedbackCsat.tsx, there is already a property called isNewListeningExperience
    // so I am giving this event a new property to distinguish
    isRehaul: boolean;
  }>;
  [AnalyticsEventKey.openedDocumentSettings]: Properties<{
    state: 'locked' | 'unlocked';
  }>;
  [AnalyticsEventKey.playerJumpBySentence]: Properties<{
    backward: boolean;
  }>;
  [AnalyticsEventKey.playerSpeedChanged]: Properties<{
    speed: number;
    wpm: number;
    source?: 'speedUI' | 'keyboardShortcut' | 'autoIncrease' | 'switchToFree' | 'switchToNonPremium' | 'settingsSync';
  }>;
  [AnalyticsEventKey.playerSpeedRampChanged]: Properties<{
    value: boolean;
  }>;
  [AnalyticsEventKey.playerVoiceChanged]: Properties<{
    voiceId: string;
    voiceName: string;
    isHD: boolean;
    engine?: string;
  }>;
  [AnalyticsEventKey.topNavItemClicked]: Properties<{
    tab: ListeningScreenTabs | null;
  }>;
  [AnalyticsEventKey.upgradePressedDashboard]: Properties<{
    source: string;
    vcw?: boolean;
  }>;
  [AnalyticsEventKey.upsellModalsContextual]: Properties<{
    variant: string;
  }>;
  [AnalyticsEventKey.askAISummaryFeedbackSubmitted]: Properties<{
    feedback: string;
    itemId: string;
  }>;
  [AnalyticsEventKey.askAISummaryRatingSubmitted]: Properties<{
    rating: 'like' | 'dislike';
  }>;
  [AnalyticsEventKey.userSignedOut]: Properties<AuthEventProperties>;
  [AnalyticsEventKey.userSignedIn]: Properties<AuthEventProperties>;
  [AnalyticsEventKey.userAuthenticationFailed]: Properties<AuthEventProperties>;
  [AnalyticsEventKey.checkoutModalOpened]: Properties<{
    source: string;
  }>;
  [AnalyticsEventKey.userAuthenticationLegacy]: Properties<{
    source: 'email' | 'customToken' | 'google' | 'apple' | 'facebook' | 'Anonymous';
    mode: 'login' | 'sign_up';
  }>;
  [AnalyticsEventKey.helpUsImprove]: Properties<{
    feedback: string;
  }>;
  [AnalyticsEventKey.libraryFileUploadStarted]: Properties<{
    source: string;
    name: string;
    size: number;
  }>;
  [AnalyticsEventKey.libraryFileUploadFailed]: Properties<{
    source: string;
    name: string;
    size: number;
  }>;
  [AnalyticsEventKey.libraryFileReuploadStarted]: Properties<{
    name: string;
    size: number;
  }>;
  [AnalyticsEventKey.libraryFileUploadCancelled]: Properties<{
    itemId: string;
  }>;
  [AnalyticsEventKey.libraryImportDialogSourceOpened]: Properties<{
    source: string;
    dialogUsedFrom: string;
  }>;
  [AnalyticsEventKey.libraryIntegrationFileImportStarted]: Properties<{
    source: string;
    name: string;
    size: number;
  }>;
  [AnalyticsEventKey.libraryIntegrationFileImportFailed]: Properties<{
    source: string;
    name: string;
    size: number;
  }>;
  [AnalyticsEventKey.librarySuggestionImportClicked]: Properties<{
    source: string;
    name: string;
  }>;
  [AnalyticsEventKey.libraryImportSuggestionDismissed]: Properties<{
    source: string;
    name: string;
  }>;
  [AnalyticsEventKey.libraryItemsMoved]: Properties<{
    count: number;
    to: string;
  }>;
  [AnalyticsEventKey.libraryItemsRemoved]: Properties<{
    count: number;
  }>;
  [AnalyticsEventKey.librarySortingChanged]: Properties<{
    sortBy: string;
    sortOrder: string;
  }>;
  [AnalyticsEventKey.libraryFilterChanged]: Properties<{
    value: string;
  }>;
  [AnalyticsEventKey.libraryViewModeChanged]: Properties<{
    value: string;
  }>;
  [AnalyticsEventKey.libraryShowFolderTypeChanged]: Properties<{
    value: string;
  }>;
  [AnalyticsEventKey.libraryThemeToggled]: Properties<{
    value: string;
  }>;
  [AnalyticsEventKey.libraryBreadcrumbsNavigated]: Properties<{}>;
  [AnalyticsEventKey.libraryItemRenamed]: Properties<{
    name: string;
    itemId: string;
  }>;
  [AnalyticsEventKey.libraryFileShared]: Properties<{
    name: string;
    itemId: string;
  }>;
  [AnalyticsEventKey.libraryShareDocument]: Properties<{
    link: string;
  }>;
  [AnalyticsEventKey.libraryFileOriginalUrlOpened]: Properties<{
    name: string;
    itemId: string;
  }>;
  [AnalyticsEventKey.libraryFolderCreated]: Properties<{
    name: string;
  }>;
  [AnalyticsEventKey.libraryContextMenuActionClicked]: Properties<{
    action: 'new-folder' | 'upload-file' | 'create-text' | 'paste-link' | 'import-from-google-drive' | 'import-from-dropbox' | 'import-from-onedrive';
  }>;
  [AnalyticsEventKey.voiceoverCrossSellPressed]: Properties<{
    source: 'new' | 'card';
  }>;
  [AnalyticsEventKey.mp3DownloadRateLimited]: Properties<{}>;
  [AnalyticsEventKey.trialStarted]: Properties<{
    plan: string;
    trialLength: number;
    currency: string;
    value: number;
    app_platform: string;
  }>;
  [AnalyticsEventKey.purchaseCompleted]: Properties<{
    plan: string;
    trialLength: number;
    currency: string;
    value: number;
    app_platform: string;
  }>;
} & AudioDownloadAnalyticsEventProperties &
  VoicesLibraryAnalyticsEventProperties &
  TableOfContentsAnalyticsEventProperties &
  NoteHighlightAnalyticsEventProperties;

export type AnalyticsEventProperty<Key extends AnalyticsEventKey> = Key extends keyof AnalyticsEventProperties ? AnalyticsEventProperties[Key] : {};
